/**
 * Shared JS imports — pure components common to all themes.
 *
 * Each theme's own index.js imports this file via the @hyperio alias,
 * then adds theme-specific extras (canvas animations, mermaid config, etc.).
 *
 * No build pipeline lives here — these are plain source files consumed
 * by the webpack builds of the standalone theme bundles (website-arcate,
 * website-elodiejoyatelier, website-kawaii).
 */

// Core
import './core/base.js';

// Navbar
import './components/navbar/navbar.js';

// Forms
import './components/forms/ajax-forms.js';
import './components/forms/datetime-input.js';
import './components/forms/input-google-picker.js';
import './components/forms/tags-input.js';
// The visitor's calendar day into the hidden fields that ask for it — a server on UTC does not
// know what day it is where the visitor stands, and a day count is off by one without it.
import './components/forms/local-today.js';
// Tient la position de lecture d'un bord à l'autre d'une soumission. Vient de l'éditeur de
// formulaires, où le saut se voyait le plus, mais il n'y avait rien de propre à cet outil : toute
// page qui poste et se re-rend repart en haut, et le lecteur perd le contrôle qu'il vient d'actionner.
import './components/forms/hold-position.js';
// Verrouille le bouton d'envoi tant qu'un champ obligatoire est vide, [data-required] compris —
// donc les champs CACHÉS, que la validation native du navigateur ne regarde pas. Il vivait en
// double dans website-arcate et website-elodiejoyatelier, aux mêmes octets près : c'est un
// comportement de formulaire, pas une allure, et le thème kawaii en a l'usage lui aussi (les
// lat/lon que remplit l'autocomplétion de ville sur les formulaires d'outils astro).
import './components/form-required/form-required.js';
// Une soumission qui prend du temps : un spinner sur le bouton passé un court délai — donc seulement
// là où la réponse se fait attendre — et la porte fermée derrière elle, un second envoi du même
// formulaire étant un second calcul, voire un second acte.
import './components/forms/submit-busy.js';

// A document being written keeps itself in the browser — the landing composer's field
// (data-keep-draft). localStorage, per origin, no /my-data listing: a draft is where one left off.
import './components/forms/keep-draft.js';

// The preview of a composed landing page renders as a page, in a frame of its own
// (data-landing-preview): the theme's rules for a page's sections apply there, not the panel's.
import './components/landing-preview/landing-preview.js';

// Bouton « copier » (data-copy="<sélecteur>") — une valeur qu'on doit emporter : le mot de passe
// généré, une clé d'API. Délégué sur document, donc valable aussi pour ce qui est rendu après coup.
import './components/clipboard/copy-value.js';

// The device's share sheet, on the buttons that ask for it (a phone, mostly), and the two analytics
// events a site of tools cannot do without — a run, a share.
import './components/share/web-share.js';
import './components/analytics/tool-events.js';

// Rich Editor
import { initializeRichEditors } from './components/rich-editor/index.js';

document.addEventListener('DOMContentLoaded', () => {
    try {
        document.execCommand('defaultParagraphSeparator', false, 'p');
        initializeRichEditors();
    } catch (error) {
        console.error('Erreur lors de l\'initialisation des editeurs riches:', error);
    }
});

// UI Components
import './components/modal/modal.js';
import './components/tabs/tabs.js';
import './components/breadcrumb/breadcrumb.js';

// Iframe
import './components/iframe/iframe.js';

// A treatment followed from its page (data-job-monitor): an indicator, a poll of the result at the
// rhythm the server names, one reload when it settles. The no-JS fallback is the page's own meta refresh.
import './components/job-monitor/job-monitor.js';

// Layouts

// Rich embeds (YouTube, Vimeo, Spotify, maps, oEmbed, etc.)
import './components/rich-embed/rich-embed.js';

// Download a generated folio in the selected representation.
import './components/folio-delivery/folio-delivery.js';

// Theme diagnostics helper
